/**
 * How the family home is treated for the hotelling and non-clinical care contributions, said for
 * THIS client rather than as three "if" cases (owner, 15 Oct 2026: the box also showed for a client
 * with no home at all). Null where nobody in the household owns a home — then there is nothing to
 * say. The exemption itself is the engine's own rule (hasAgedCareProtectedPerson), so the fee and the
 * explanation of the fee cannot disagree.
 */
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { StrategyOptions } from '../../../../shared/src/calculation/strategyOptionsTypes.ts';
import { hasAgedCareProtectedPerson } from '../../../../shared/src/calculation/entityHomeRules.ts';
import { partnerRemainsInFamilyHome } from '../../../../shared/src/calculation/householdStatus.ts';
import { anyoneOwnsHome } from '../../../../shared/src/calculation/householdPosition.ts';

export interface HomeTreatmentWords {
    /** The answer, bold: "Tammy lives in the home, so it is not counted". */
    lead: string;
    /** The rest of the lead sentence. */
    leadRest: string;
    /** What would change it. */
    change: string;
}

export function homeTreatmentWords(factFind: FactFind, strategyOptions: StrategyOptions = {}): HomeTreatmentWords | null {
    if (!anyoneOwnsHome(factFind)) return null;
    const exempt = hasAgedCareProtectedPerson(factFind, strategyOptions);
    const partnerStays = partnerRemainsInFamilyHome(factFind, strategyOptions);
    const forWhom = (factFind.goals as { aged_care_for?: string } | undefined)?.aged_care_for;
    const stayingName = partnerStays
        ? ((forWhom === 'partner' ? factFind.client?.firstName : factFind.partner?.firstName) || 'your partner')
        : null;
    const capped = 'the home is counted, but only up to the capped amount (not the full value)';
    if (exempt && stayingName) {
        return {
            lead: `${stayingName} lives in the home, so it is not counted`,
            leadRest: ' in the aged care assets assessment while they are there.',
            change: `If that changes — for example ${stayingName} later moves out — ${capped}, which can increase your HSC and NCCC. The funding options page lets you test that.`,
        };
    }
    if (exempt) {
        return {
            lead: 'A protected person lives in the home, so it is not counted',
            leadRest: ' in the aged care assets assessment while they are there.',
            change: `If that changes — if they later move out — ${capped}, which can increase your HSC and NCCC.`,
        };
    }
    return {
        lead: 'No protected person lives in the home, so it is counted — but only up to the capped amount',
        leadRest: ' (not the full value) in the aged care assets assessment.',
        change: 'If a protected person — a partner, a dependent child, a carer or a close relative — lives there, the home is not counted at all.',
    };
}
